import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Eye, Mail, Star, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn, Badge } from "../components/common";
import { Modal, Field, Selecter, TextArea, braceIssues, BracesWarning } from "./crm/crmui";

const CAT_META = {
  welcome: { label: "Boas-vindas", color: "bg-emerald-100 text-emerald-700" },
  renewal: { label: "Renovacao", color: "bg-blue-100 text-blue-700" },
  cancellation: { label: "Cancelamento", color: "bg-red-100 text-red-700" },
  invoice: { label: "Fatura", color: "bg-amber-100 text-amber-700" },
  reminder: { label: "Lembrete", color: "bg-purple-100 text-purple-700" },
  custom: { label: "Custom", color: "bg-slate-100 text-slate-700" },
};

const STARTER_HTML = `<h2>Bem-vindo(a) a plataforma {{plataforma}}</h2>
<p>Ola {{cliente_nome}},</p>
<p>Estamos felizes por o(a) ter connosco. A sua subscricao iniciou-se a {{data_inicio}}, com um custo mensal de {{mrr}} {{moeda}}.</p>
<p>Aceda ao portal: <a href="{{link_portal}}">{{link_portal}}</a></p>
<p>Cumprimentos,<br>Equipa Cogniseg</p>`;

const EMPTY = { nome: "", canal: "email", categoria: "welcome", plataforma: "", assunto: "", corpo_html: STARTER_HTML, corpo_texto: "", ativo: false };
const PLATAFORMAS = ["", "Cognietica", "Privaxia", "Assiduo", "Recrutai"];
const CANAIS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

export default function EmailTemplates() {
  const [rows, setRows] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [filterPlat, setFilterPlat] = useState("");
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [standardVars, setStandardVars] = useState([]);
  const [sendModal, setSendModal] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (filterCat) params.categoria = filterCat;
      if (filterPlat) params.plataforma = filterPlat;
      const [{ data }, v] = await Promise.all([
        api.get("/email-templates", { params }),
        api.get("/email-templates/vars"),
      ]);
      setRows(data || []);
      setStandardVars(v.data.standard || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [filterCat, filterPlat]); // eslint-disable-line

  const salvar = async () => {
    if (!edit.nome.trim() || !edit.assunto.trim()) return toast.error("Indique nome e assunto.");
    try {
      if (edit.id) await api.put(`/email-templates/${edit.id}`, edit);
      else await api.post("/email-templates", edit);
      toast.success("Guardado."); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remover = async (t) => {
    if (!confirm(`Eliminar "${t.nome}"?`)) return;
    try {
      await api.delete(`/email-templates/${t.id}`);
      toast.success("Eliminado."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doPreview = async (t) => {
    try {
      const { data } = await api.post("/email-templates/render", { template_id: t.id, extras: { plataforma: t.plataforma || "Cognietica", cliente_nome: "Cliente Exemplo", mrr: 250, moeda: "EUR", data_inicio: new Date().toISOString().slice(0, 10) } });
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openSend = async (t) => {
    setSendModal({ template: t, account_id: "", extras: {} });
    if (accounts.length === 0) {
      try {
        const { data } = await api.get("/crm/accounts/lookup?limit=200");
        setAccounts(data || []);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }
  };

  const doSend = async () => {
    if (!sendModal?.account_id) return toast.error("Selecione uma conta.");
    setSending(true);
    try {
      const { data } = await api.post(`/email-templates/${sendModal.template.id}/send`, {
        account_id: sendModal.account_id,
        plataforma: sendModal.template.plataforma,
      });
      if (data.estado === "enviado") toast.success(`Enviado para ${data.dest}`);
      else toast.warning(`Estado: ${data.estado} - ${data.dest}`);
      setSendModal(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const insertVar = (v) => setEdit((e) => ({ ...e, corpo_html: (e.corpo_html || "") + `{{${v}}}` }));

  const grouped = useMemo(() => {
    const map = {};
    rows.forEach((r) => { (map[r.categoria] = map[r.categoria] || []).push(r); });
    return map;
  }, [rows]);

  return (
    <>
      <PageHeader
        overline="Comunicacoes"
        title="Templates de Email"
        action={<Btn onClick={() => setEdit({ ...EMPTY })} testid="new-email-tpl-btn"><Plus size={16} /> Novo template</Btn>}
      />

      <div className="flex items-center gap-3 mb-4 bg-white border border-tint rounded-lg px-3 py-2">
        <span className="text-xs text-muted2">Filtros:</span>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="filter-cat" className="text-xs border border-tint rounded px-2 py-1">
          <option value="">Todas as categorias</option>
          {Object.entries(CAT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <select value={filterPlat} onChange={(e) => setFilterPlat(e.target.value)} data-testid="filter-plat" className="text-xs border border-tint rounded px-2 py-1">
          <option value="">Todas plataformas</option>
          {PLATAFORMAS.filter(Boolean).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="flex-1" />
        <div className="text-[11px] text-muted2">Automatico: welcome (nova subscricao), renewal (renovacao), cancellation (cancelamento).</div>
      </div>

      {rows.length === 0 && <div className="text-center text-muted2 border border-tint rounded-lg py-12">Sem templates de email.</div>}

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="mb-6" data-testid={`email-cat-${cat}`}>
          <div className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-2 ${CAT_META[cat]?.color || "bg-slate-100"}`}>
            {CAT_META[cat]?.label || cat}
          </div>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {items.map((t) => (
              <div key={t.id} data-testid={`email-tpl-${t.id}`} className="border border-tint rounded-lg p-3 bg-white hover:shadow-sm transition">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-navy text-sm truncate flex items-center gap-1.5">
                      <Mail size={13} /> {t.nome}
                    </div>
                    <div className="text-[10px] text-muted2 mt-0.5 flex items-center gap-2">
                      <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${t.canal === "sms" ? "bg-purple-100 text-purple-700" : "bg-blue-50 text-blue-700"}`}>{(t.canal || "email").toUpperCase()}</span>
                      <span>{t.plataforma || "generico"}</span>
                      {t.ativo && <span className="inline-flex items-center gap-1 text-amber-700"><Star size={9} /> ativo</span>}
                    </div>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 mt-2 line-clamp-2">{t.assunto}</div>
                <div className="flex items-center gap-1 mt-3">
                  <button onClick={() => setEdit({ ...t })} data-testid={`email-tpl-edit-${t.id}`}
                    className="text-[11px] px-2 py-1 rounded bg-blue text-white hover:bg-[var(--blue-hover)]">Editar</button>
                  <button onClick={() => doPreview(t)} data-testid={`email-tpl-preview-${t.id}`}
                    className="text-[11px] px-2 py-1 rounded border border-tint text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"><Eye size={11} /></button>
                  <button onClick={() => openSend(t)} data-testid={`email-tpl-send-${t.id}`}
                    className="text-[11px] px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"><Send size={11} /> Testar</button>
                  <button onClick={() => remover(t)} data-testid={`email-tpl-delete-${t.id}`}
                    className="text-[11px] px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"><Trash2 size={11} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Editar template" : "Novo template"} testid="email-tpl-modal" size="xl">
        {edit && (
          <>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Nome" value={edit.nome} onChange={(v) => setEdit({ ...edit, nome: v })} testid="tpl-nome" />
              <Selecter label="Canal" value={edit.canal || "email"} onChange={(v) => setEdit({ ...edit, canal: v })} options={CANAIS} testid="tpl-canal" />
              <Selecter label="Categoria" value={edit.categoria} onChange={(v) => setEdit({ ...edit, categoria: v })} options={Object.entries(CAT_META).map(([k, m]) => ({ value: k, label: m.label }))} testid="tpl-cat" />
              <Selecter label="Plataforma" value={edit.plataforma} onChange={(v) => setEdit({ ...edit, plataforma: v })} options={[{ value: "", label: "- Generico -" }, ...PLATAFORMAS.filter(Boolean)]} testid="tpl-plat" />
            </div>
            {edit.canal !== "sms" && (
              <div>
                <Field label="Assunto" value={edit.assunto} onChange={(v) => setEdit({ ...edit, assunto: v })} placeholder="Bem-vindo a {{plataforma}}" testid="tpl-assunto" />
                <BracesWarning issue={braceIssues(edit.assunto, standardVars)} testid="tpl-assunto-warn" />
              </div>
            )}
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!edit.ativo} onChange={(e) => setEdit({ ...edit, ativo: e.target.checked })} data-testid="tpl-ativo" />
              Marcar como template ativo (usado no automatismo desta categoria/plataforma)
            </label>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">{edit.canal === "sms" ? "Corpo SMS (max 160 caract.)" : "Corpo HTML"}</label>
              <textarea value={edit.canal === "sms" ? edit.corpo_texto : edit.corpo_html}
                onChange={(e) => setEdit({ ...edit, ...(edit.canal === "sms" ? { corpo_texto: e.target.value } : { corpo_html: e.target.value }) })}
                data-testid="tpl-body" rows={edit.canal === "sms" ? 4 : 12}
                className="w-full px-3 py-2 border border-tint rounded-md text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue/20" />
              <BracesWarning issue={braceIssues(edit.canal === "sms" ? edit.corpo_texto : edit.corpo_html, standardVars)} testid="tpl-body-warn" />
            </div>
            <div className="bg-slate-50 border border-tint rounded-md p-2">
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Variaveis (clique para inserir)</div>
              <div className="flex flex-wrap gap-1">
                {standardVars.map((v) => (
                  <button key={v} type="button" onClick={() => insertVar(v)} data-testid={`ins-${v}`}
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white border border-tint hover:border-blue hover:text-blue">{v}</button>
                ))}
              </div>
            </div>
            <TextArea label="Corpo texto puro (fallback)" value={edit.corpo_texto} onChange={(v) => setEdit({ ...edit, corpo_texto: v })} testid="tpl-texto" />
            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white -mx-6 px-6 py-3 border-t border-tint">
              <Btn variant="ghost" onClick={() => setEdit(null)}>Cancelar</Btn>
              <Btn variant="ghost" onClick={() => edit.id && doPreview(edit)}><Eye size={14} /> Preview</Btn>
              <Btn onClick={salvar} testid="tpl-save"><Save size={14} /> Guardar</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!preview} onClose={() => setPreview(null)} title="Pre-visualizacao" testid="email-preview-modal" size="lg">
        {preview && (
          <>
            <div className="bg-slate-50 border border-tint rounded-md p-3 mb-2">
              <div className="text-[10px] uppercase text-muted2">Assunto</div>
              <div className="font-semibold text-navy">{preview.assunto}</div>
            </div>
            <div className="border border-tint rounded-md p-3 bg-white max-h-[60vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: preview.corpo_html }} />
          </>
        )}
      </Modal>

      <Modal open={!!sendModal} onClose={() => setSendModal(null)} title="Enviar teste" testid="send-modal">
        {sendModal && (
          <>
            <p className="text-xs text-muted2">Envia este template para o email da conta selecionada usando os dados reais dela.</p>
            <Selecter label="Conta destinataria" value={sendModal.account_id} onChange={(v) => setSendModal({ ...sendModal, account_id: v })}
              options={[{ value: "", label: "- Selecione -" }, ...accounts.map((a) => ({ value: a.id, label: `${a.nome}${a.email ? ` (${a.email})` : " (sem email)"}` }))]}
              testid="send-account" />
            <div className="text-[11px] text-muted2 bg-slate-50 border border-tint rounded p-2">
              Template: <strong>{sendModal.template.nome}</strong><br />
              Assunto: {sendModal.template.assunto}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setSendModal(null)}>Cancelar</Btn>
              <Btn onClick={doSend} testid="send-confirm" disabled={sending}><Send size={14} /> {sending ? "A enviar..." : "Enviar agora"}</Btn>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
